import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WorkbookInstance } from '@fortune-sheet/react';
import { toast } from 'react-toastify';
import { fileBytesToFortuneSheets, downloadAsXlsx, downloadAsCsv, toCelldataSheet } from './converters';
import { FortuneSheet } from './types';
import { saveDocument } from '../../store/investor/investorApiHelper';

export interface UseSheetEditorDrawerLogicArgs {
    open: boolean;
    fileName: string;
    /** In-memory sheets (import / composed). When provided, skips the URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — passed through to the save payload
     *  so a new version groups with its siblings (mirrors the Plate editor's
     *  persist path in ReportEditorRoute). */
    genId?: string;
    /** Save context; absent for the read-only viewer (Save disabled). */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    documentType?: string;
    name?: string;
    description?: string;
    /** Called with the saved document after a successful save. */
    onSaved?: (doc: any) => void;
    /** Called with the saved document after a name/description edit (the header's
     *  "Edit name & description" dialog) succeeds. Unlike `onSaved`, this does not
     *  imply the drawer should close — the caller only needs to refresh its own
     *  list view; the drawer already reflects the new name via `currentName`. */
    onRenamed?: (doc: any) => void;
}

export const EMPTY_SHEET: FortuneSheet = { name: 'Sheet1', id: '0', order: 0, status: 1, celldata: [], row: 50, column: 26 };

/**
 * Owns the sheet-editor drawer's non-UI concerns: resolve sheets (in-memory or
 * fetched), track loading/error, and expose download + save handlers. Keeps
 * SheetEditorDrawer focused on composition/JSX.
 */
export const useSheetEditorDrawerLogic = ({
    open,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    documentType,
    name,
    description,
    onSaved,
    onRenamed,
}: UseSheetEditorDrawerLogicArgs) => {
    const [fetchedSheets, setFetchedSheets] = useState<FortuneSheet[] | null>(null);
    const [loading, setLoading] = useState(!inMemorySheets);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const workbookRef = useRef<WorkbookInstance>(null);

    // Own the current name/description here (seeded from props) rather than
    // reading the props directly in handleSave — a rename updates these in
    // place so a later plain Save persists the edited name, not the stale one
    // the drawer was opened with.
    const [currentName, setCurrentName] = useState(name);
    const [currentDescription, setCurrentDescription] = useState(description);

    // When in-memory sheets are provided, use them directly — no fetch needed.
    // Normalise to celldata: a saved sheet round-trips as the runtime 2D `data`
    // (from getAllSheets()), which <Workbook> can't initialise from — without
    // this a reopened saved sheet renders an empty grid.
    const rawSheets = inMemorySheets ?? fetchedSheets;
    const sheets = useMemo(() => (rawSheets ? rawSheets.map(toCelldataSheet) : rawSheets), [rawSheets]);

    useEffect(() => {
        // In-memory path: nothing to fetch.
        if (inMemorySheets) {
            setLoading(false);
            setError(null);
            return;
        }

        if (!open || !fileUrl) return;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setFetchedSheets(null);

        (async () => {
            try {
                const response = await fetch(fileUrl, { signal: controller.signal });
                if (!response.ok) throw new Error('Failed to fetch file');
                const buffer = await response.arrayBuffer();
                const parsed = await fileBytesToFortuneSheets(buffer, mimeType, fileName);
                setFetchedSheets(parsed.length ? parsed : [EMPTY_SHEET]);
                setLoading(false);
            } catch (err) {
                if ((err as DOMException)?.name === 'AbortError') return;
                setError(err instanceof Error ? err.message : 'Failed to load file');
                setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [open, fileUrl, mimeType, inMemorySheets]);

    /** Latest sheet state: prefer the live workbook, fall back to resolved sheets. */
    const currentSheets = (): FortuneSheet[] => {
        const live = workbookRef.current?.getAllSheets();
        return live && live.length ? live : sheets ?? [];
    };

    const handleDownloadXlsx = async () => {
        const data = currentSheets();
        if (!data.length) return;
        await downloadAsXlsx(data, fileName);
        toast.success('Downloaded as .xlsx');
    };

    const handleDownloadCsv = () => {
        const data = currentSheets();
        if (!data.length) return;
        const active = data.find((s) => s.status === 1) ?? data[0];
        downloadAsCsv(active, fileName);
        toast.success('Downloaded active sheet as .csv');
    };

    /** Returns true only on a successful save — the drawer closes on that signal. */
    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!assesseeOrgId) return false; // no save context (read-only viewer)
        // Persist the celldata init form, not the runtime 2D `data` from
        // getAllSheets() — otherwise the saved doc reopens as an empty grid.
        const data = currentSheets().map(toCelldataSheet);
        if (!data.length) return false;
        setSaving(true);
        try {
            const savedDoc = await saveDocument({
                assesseeOrgId,
                // DocumentRequest binds the mongo id as `id`; sending `docId` is ignored
                // and the save creates a duplicate instead of updating the existing doc.
                ...(docId ? { id: docId } : {}),
                ...(genId ? { genId } : {}),
                ...(investorOrgId ? { investorOrgId } : {}),
                ...(createdBy ? { createdBy } : {}),
                ...(documentType ? { documentType } : {}),
                ...(currentName ? { name: currentName } : {}),
                ...(currentDescription ? { description: currentDescription } : {}),
                editor: 'sheet',
                formatType: 'XLSX',
                content: { version: 1, editor: 'sheet', value: data },
            });
            toast.success('Saved');
            onSaved?.(savedDoc);
            return true;
        } catch (err) {
            console.error('[useSheetEditorDrawerLogic] save failed', err);
            toast.error('Failed to save');
            return false;
        } finally {
            setSaving(false);
        }
    }, [assesseeOrgId, docId, genId, investorOrgId, createdBy, documentType, currentName, currentDescription, onSaved]);

    // Persists a name/description edit from the header's "Edit name & description"
    // dialog — the same saveDocument call handleSave makes, carrying the CURRENT
    // sheet content through (mirrors PlateDocumentDrawer's rename, which also
    // commits the live editor content alongside the metadata change).
    const handleRename = useCallback(
        async (newName: string, newDescription: string) => {
            if (!assesseeOrgId) return;
            const data = currentSheets().map(toCelldataSheet);
            try {
                const savedDoc = await saveDocument({
                    assesseeOrgId,
                    ...(docId ? { id: docId } : {}),
                    ...(genId ? { genId } : {}),
                    ...(investorOrgId ? { investorOrgId } : {}),
                    ...(createdBy ? { createdBy } : {}),
                    ...(documentType ? { documentType } : {}),
                    name: newName,
                    description: newDescription,
                    editor: 'sheet',
                    formatType: 'XLSX',
                    content: { version: 1, editor: 'sheet', value: data },
                });
                setCurrentName(newName);
                setCurrentDescription(newDescription);
                onRenamed?.(savedDoc);
            } catch (err) {
                console.error('[useSheetEditorDrawerLogic] rename failed', err);
                toast.error('Could not rename the report. Please try again.');
            }
        },
        [assesseeOrgId, docId, genId, investorOrgId, createdBy, documentType, onRenamed],
    );

    return {
        sheets,
        loading,
        error,
        saving,
        workbookRef,
        handleDownloadXlsx,
        handleDownloadCsv,
        handleSave,
        currentName,
        currentDescription,
        handleRename,
    };
};
